import React, { useRef, useState } from "react";
import { TextField, InputAdornment, IconButton, TextFieldProps as MuiTextFieldProps, SxProps, Theme, Typography, Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useStoreConfig } from "../contexts/StoreConfigContext";




export interface BasicPasswordFieldProps extends Omit<MuiTextFieldProps, "type" | "onChange"> {
    onChange?: MuiTextFieldProps['onChange'];
}


export const BasicPasswordField: React.FC<BasicPasswordFieldProps> = (props) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const { config } = useStoreConfig();

    const handleClickShowPassword = () => {
        // Store cursor position
        const cursorPosition = inputRef.current?.selectionStart || 0;

        // Toggle password visibility
        setShowPassword(!showPassword);

        // Use setTimeout to restore cursor position after re-render
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.selectionStart = cursorPosition;
                inputRef.current.selectionEnd = cursorPosition;
            }
        }, 0);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    // Extract label to render separately
    const { label, required, ...textFieldProps } = props;

    return (
        <Box sx={{ width: '100%' }}>
            {label && (
                <Typography
                    variant='subtitle2'
                    fontWeight={600}
                    fontFamily='primaryFont'
                    sx={{
                        mb: 0.75,
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        letterSpacing: 0.3,
                    }}
                >
                    {label}
                    {required && <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>}
                </Typography>
            )}
            <TextField
                {...textFieldProps}
                type={showPassword ? "text" : "password"}
                inputRef={inputRef}
                InputProps={{
                    ...props.InputProps,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    "& .MuiInputLabel-root": {
                        color: "grey",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                        color: "grey",
                    },
                    "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                            borderColor: "grey",
                        },
                        "&:hover fieldset": {
                            borderColor: "grey",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "grey",
                        },
                    },
                    "& .MuiInputBase-input": {
                        fontSize: '0.875rem',
                        fontFamily: 'primaryFont',
                        letterSpacing: 0.3,
                        padding: '10px 14px',
                    },
                    // Only apply custom autofill styling if primaryColor is not black
                    ...(config.primaryColor !== "#000000" && {
                        "& .MuiInputBase-input:-webkit-autofill": {
                            boxShadow: `0 0 0 100px ${config.primaryColor}25 inset !important`,
                            color: "inherit !important",
                            caretColor: "inherit",
                        }
                    }),
                    ...(props.sx || {}),
                }}
            />
        </Box>
    );
}; 